import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, Building2, ChevronsUpDown, LayoutGrid, LogOut, Palette, Plug, Search, Shield, User, Users } from 'lucide-react';
import { Avatar } from '../../ui/primitives.tsx';
import { useLogout, useNotifications } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';

const PANEL_WIDTH = 288;
const MARGIN = 12;

/**
 * Account button + dropdown. Mobile: an avatar in each page's topbar. Desktop: a row at the bottom of
 * the sidebar (avatar, name, notification badge), so nothing floats over the page content.
 *
 * The panel itself is portaled to <body> as `position: fixed`, positioned from the trigger's own
 * measured rect rather than CSS `right: 0` on a narrow anchor — that would push it off-screen on
 * wide phones where the trigger sits close to the edge with little room to its left.
 */
export function AccountMenu({ variant = 'topbar' }: { variant?: 'topbar' | 'sidebar' }) {
  const { me, org, detail, base, orgId, openHomeCustomize, openSearch } = useOrgCtx();
  const navigate = useNavigate();
  const logout = useLogout();
  const unread = useNotifications(orgId).data?.unread ?? 0;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; right?: number; left?: number }>({ top: 0, right: MARGIN });
  const triggerRef = useRef<HTMLButtonElement>(null);

  const place = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    if (variant === 'sidebar') {
      // Opens upward from the bottom of the sidebar, aligned with the row's left edge.
      setPos({ bottom: window.innerHeight - r.top + 8, left: r.left });
      return;
    }
    const right = Math.max(MARGIN, window.innerWidth - r.right);
    setPos({ top: r.bottom + 10, right: Math.min(right, window.innerWidth - PANEL_WIDTH - MARGIN) });
  };

  useEffect(() => {
    if (!open) return;
    place();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div className={`account-menu-anchor${variant === 'sidebar' ? ' in-sidebar' : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        className={variant === 'sidebar' ? 'account-row' : 'account-trigger'}
        aria-label={unread ? `Account e impostazioni, ${unread} notifiche da leggere` : 'Account e impostazioni'}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {variant === 'sidebar' ? (
          <>
            <Avatar id={me.user.id} name={me.user.displayName} avatarUrl={me.user.avatarUrl} size={28} />
            <span className="account-row-name">{me.user.displayName}</span>
            {unread > 0 && <span className="badge">{unread > 99 ? '99+' : unread}</span>}
          </>
        ) : (
          <>
            <Avatar id={me.user.id} name={me.user.displayName} avatarUrl={me.user.avatarUrl} size={34} />
            {unread > 0 && <span className="badge account-trigger-badge">{unread > 99 ? '99+' : unread}</span>}
          </>
        )}
      </button>
      {open && createPortal(
        <>
          <div className="account-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="account-panel" role="menu" aria-label="Account" style={{ ...pos, width: PANEL_WIDTH }}>
            <div className="account-panel-header">
              <Avatar id={me.user.id} name={me.user.displayName} avatarUrl={me.user.avatarUrl} size={40} />
              <span className="grow">
                <div className="name">{me.user.displayName}</div>
                <div className="email">{me.user.email}</div>
              </span>
            </div>

            {me.organizations.length > 1 && (
              <>
                <div className="heading">Organizzazione</div>
                {me.organizations.map((o) => (
                  <button key={o.id} type="button" className="account-menu-item" role="menuitem" onClick={() => go(`/o/${o.slug}`)}>
                    <span className="icon"><Building2 size={17} /></span>
                    <span className="grow">{o.name}</span>
                    {o.id === org.id ? <span className="dot" /> : <ChevronsUpDown size={14} color="var(--label-3)" />}
                  </button>
                ))}
                <hr className="sep" />
              </>
            )}

            <button type="button" className="account-menu-item" role="menuitem" onClick={() => { setOpen(false); openSearch(); }}>
              <span className="icon"><Search size={17} /></span>
              <span className="grow">Cerca</span>
              <span className="kbd">⌘K</span>
            </button>
            <button type="button" className="account-menu-item" role="menuitem" onClick={() => go(`${base}/inbox`)}>
              <span className="icon"><Bell size={17} /></span>
              <span className="grow">Notifiche</span>
              {unread > 0 && <span className="badge">{unread > 99 ? '99+' : unread}</span>}
            </button>
            <button type="button" className="account-menu-item" role="menuitem" onClick={() => { setOpen(false); openHomeCustomize(); }}>
              <span className="icon"><LayoutGrid size={17} /></span>
              <span className="grow">Personalizza Home</span>
            </button>
            <hr className="sep" />

            <button type="button" className="account-menu-item" role="menuitem" onClick={() => go(`${base}/settings/profile`)}>
              <span className="icon"><User size={17} /></span><span className="grow">Profilo</span>
            </button>
            <button type="button" className="account-menu-item" role="menuitem" onClick={() => go(`${base}/settings/appearance`)}>
              <span className="icon"><Palette size={17} /></span><span className="grow">Aspetto</span>
            </button>
            <button type="button" className="account-menu-item" role="menuitem" onClick={() => go(`${base}/settings/security`)}>
              <span className="icon"><Shield size={17} /></span><span className="grow">Sicurezza</span>
            </button>
            <button type="button" className="account-menu-item" role="menuitem" onClick={() => go(`${base}/settings/integrations`)}>
              <span className="icon"><Plug size={17} /></span><span className="grow">Integrazioni</span>
            </button>
            <button type="button" className="account-menu-item" role="menuitem" onClick={() => go(`${base}/settings/members`)}>
              <span className="icon"><Users size={17} /></span><span className="grow">Membri</span>
            </button>
            {detail?.capabilities.admin && (
              <button type="button" className="account-menu-item" role="menuitem" onClick={() => go(`${base}/settings/organization`)}>
                <span className="icon"><Building2 size={17} /></span><span className="grow">Organizzazione</span>
              </button>
            )}
            <hr className="sep" />
            <button
              type="button"
              className="account-menu-item danger"
              role="menuitem"
              onClick={() => { setOpen(false); logout.mutate(undefined, { onSuccess: () => navigate('/login') }); }}
            >
              <span className="icon"><LogOut size={17} /></span><span className="grow">Esci</span>
            </button>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
